import RequirePricingAdminUser from '@/components/auth/RequirePricingAdminUser';
import DateTimeDisplay from '@/components/DateTime';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Column,
  Table,
  toast,
  useBreakpoint,
  useOpenClose,
} from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { OrganizationEmailSettingCategories, OrganizationType, PriceSheetStatus } from 'types';

import { currencyFormatter } from '../../../../../helpers/currencyFormatter';
import {
  useGetOrganizationDetailsQuery,
  useGetOrganizationEmailSettingsQuery,
} from '../../../../../services/organizations';
import {
  useDeleteOrganizationPricingHoldbacksMutation,
  useGetOrganizationPricingHoldbacksQuery,
} from '../../../../../services/pricing';
import { TableLoading } from '../../../../TableLoading';

import { CreateHoldback } from './modals/CreateHoldback';

export const Holdbacks = () => {
  const { alias = '', programType = '' } = useParams<{
    alias: string;
    programType: string;
  }>();
  const { isPhone } = useBreakpoint();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const { data, isLoading, isFetching } = useGetOrganizationPricingHoldbacksQuery(
    { alias, programType },
    { skip: !alias || !programType },
  );
  const { data: organization, isLoading: isOrganizationLoading } = useGetOrganizationDetailsQuery({ alias });
  const [deleteOrganizationPricingHoldbackMutation] = useDeleteOrganizationPricingHoldbacksMutation();
  const { data: emailSettings } = useGetOrganizationEmailSettingsQuery({ alias }, { skip: !alias });
  const hasSignatoryEmail =
    (emailSettings?.emailSettings?.find((s) => s.category === OrganizationEmailSettingCategories.signatory)?.emails
      ?.length ?? 0) > 0;
  const isMaterialsVendor = organization?.organizationTypes?.includes(OrganizationType.materials);
  const holdbacks = useMemo(() => {
    if (!data || !data?.holdbacks?.length) {
      return [];
    }
    const holdbacksByStatus = data.holdbacks.reduce((acc: any, holdback: any) => {
      acc[holdback.status] = acc[holdback.status] || [];
      acc[holdback.status].push(holdback);
      return acc;
    }, {});
    const sortByCreatedDateDesc = (a: any, b: any) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();

    return [
      ...(holdbacksByStatus[PriceSheetStatus.active] || []).toSorted(sortByCreatedDateDesc),
      ...(holdbacksByStatus[PriceSheetStatus.archived] || []).toSorted(sortByCreatedDateDesc),
    ];
  }, [data]);
  const handleArchive = async (id: string) => {
    try {
      await deleteOrganizationPricingHoldbackMutation({
        alias,
        id,
        programType,
      }).unwrap();
      toast.success('Holdback Archived successfully');
    } catch (e) {
      console.error(e);
      toast.error('Error archiving Holdback');
    }
  };

  const columnConfig: Column[] = [
    {
      heading: 'status',
      dataKey: 'status',
      render: (cell: any) => (
        <Badge
          variant={
            cell === PriceSheetStatus.active ? 'success' : cell === PriceSheetStatus.archived ? 'warning' : 'danger'
          }
          message={cell}
        />
      ),
    },
    { heading: 'Name', dataKey: 'name' },
    { heading: 'org', dataKey: 'organizationName' },
    {
      heading: 'Amount',
      dataKey: 'amount',
      render: (cell: any) => currencyFormatter.format(cell),
    },
    { heading: 'State', dataKey: 'state' },
    {
      heading: 'Created',
      dataKey: 'createdDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.createdDate} dateFormat={'DD'} excludeTime />,
    },
    {
      heading: 'Archived',
      dataKey: 'archivedDate',
      width: 150,
      render: (_cell: any, row: any) => <DateTimeDisplay value={row?.archivedDate} dateFormat={'DD'} excludeTime />,
    },
    {
      heading: '',
      render: (_cell: any, row: any) => {
        return (
          row.status === PriceSheetStatus.active && (
            <RequirePricingAdminUser>
              <Button
                size="sm"
                variant="secondary"
                tone="neutral"
                isDisabled={!hasSignatoryEmail || isMaterialsVendor}
                onClick={() => {
                  handleArchive(row.id);
                }}
              >
                Archive
              </Button>
            </RequirePricingAdminUser>
          )
        );
      },
    },
  ];
  return (
    <>
      {!hasSignatoryEmail && !isMaterialsVendor && (
        <Alert
          margin="lg 0 0"
          variant="warning"
          message="Holdback settings cannot be edited without any signatory emails."
        />
      )}
      <CreateHoldback isModalOpen={isModalOpen} closeModal={closeModal} />
      <Card.Header>
        <RequirePricingAdminUser>
          <Box direction="row" justifyContent="space-between">
            <Box />
            <Button
              as="button"
              variant="primary"
              iconPrefix="add"
              size={{ base: 'sm', desktop: 'md' }}
              aria-label="create adder"
              isDisabled={!hasSignatoryEmail || isMaterialsVendor}
              onClick={() => openModal()}
            >
              {!isPhone && 'Holdback'}
            </Button>
          </Box>
        </RequirePricingAdminUser>
      </Card.Header>
      <Box margin="0 lg 0 lg">
        {isFetching || isLoading || isOrganizationLoading ? (
          <TableLoading rows={12} />
        ) : (
          <Table
            rowKey="id"
            columns={columnConfig}
            rows={holdbacks}
            isLoading={isLoading || isFetching || isOrganizationLoading}
            isScrollable={{ y: true }}
            hasStickyHeader
          />
        )}
      </Box>
    </>
  );
};
